import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

interface RiskGaugeProps {
  score: number; // 0 to 1
  size?: number;
  label?: string;
  showValue?: boolean;
  animated?: boolean;
}

export default function RiskGauge({
  score,
  size = 120,
  label,
  showValue = true,
  animated = true,
}: RiskGaugeProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const prevScoreRef = useRef(0);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = 10;
    const radius = (size - margin * 2) / 2;
    const centerX = size / 2;
    const centerY = size / 2;
    const thickness = radius * 0.2;

    const startAngle = -Math.PI * 0.75;
    const endAngle = Math.PI * 0.75;
    const angleRange = endAngle - startAngle;

    // Background arc
    const bgArc = d3
      .arc()
      .innerRadius(radius - thickness)
      .outerRadius(radius)
      .startAngle(startAngle)
      .endAngle(endAngle)
      .cornerRadius(thickness / 2);

    svg
      .append('path')
      .attr('d', bgArc as unknown as string)
      .attr('transform', `translate(${centerX},${centerY})`)
      .attr('fill', '#1e293b');

    // Gradient
    const defs = svg.append('defs');
    const gradient = defs
      .append('linearGradient')
      .attr('id', `gauge-gradient-${Math.random().toString(36).substr(2, 9)}`)
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', centerX - radius)
      .attr('y1', centerY)
      .attr('x2', centerX + radius)
      .attr('y2', centerY);

    gradient.append('stop').attr('offset', '0%').attr('stop-color', '#22c55e');
    gradient.append('stop').attr('offset', '33%').attr('stop-color', '#eab308');
    gradient.append('stop').attr('offset', '66%').attr('stop-color', '#f97316');
    gradient.append('stop').attr('offset', '100%').attr('stop-color', '#ef4444');

    const gradientId = gradient.attr('id');

    // Value arc
    const valueArc = d3
      .arc()
      .innerRadius(radius - thickness)
      .outerRadius(radius)
      .startAngle(startAngle)
      .cornerRadius(thickness / 2);

    const valueArcPath = svg
      .append('path')
      .attr('transform', `translate(${centerX},${centerY})`)
      .attr('fill', `url(#${gradientId})`);

    if (animated) {
      const fromAngle = startAngle + angleRange * prevScoreRef.current;
      const toAngle = startAngle + angleRange * Math.min(score, 1);
      valueArcPath
        .attr('d', valueArc.endAngle(fromAngle) as unknown as string)
        .transition()
        .duration(800)
        .ease(d3.easeCubicOut)
        .attrTween('d', () => {
          const interpolate = d3.interpolate(fromAngle, toAngle);
          return (t: number) => valueArc.endAngle(interpolate(t))({ innerRadius: 0, outerRadius: 0, startAngle: 0, endAngle: 0 }) as string;
        });
    } else {
      valueArcPath.attr(
        'd',
        valueArc.endAngle(startAngle + angleRange * Math.min(score, 1)) as unknown as string
      );
    }

    // Score text
    if (showValue) {
      const scoreText = svg
        .append('text')
        .attr('x', centerX)
        .attr('y', centerY + 5)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', radius * 0.45)
        .attr('font-weight', '600')
        .attr('font-family', 'JetBrains Mono, monospace')
        .attr('fill', getScoreColor(score));

      if (animated) {
        const startValue = Math.round(prevScoreRef.current * 100);
        const endValue = Math.round(score * 100);
        scoreText
          .transition()
          .duration(800)
          .tween('text', () => {
            const i = d3.interpolateRound(startValue, endValue);
            return function (t: number) {
              d3.select(this).text(i(t));
            };
          });
      } else {
        scoreText.text(Math.round(score * 100));
      }
    }

    // Label
    if (label) {
      svg
        .append('text')
        .attr('x', centerX)
        .attr('y', centerY + radius * 0.55)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('fill', '#94a3b8')
        .attr('font-family', 'Inter, sans-serif')
        .text(label);
    }

    prevScoreRef.current = score;
  }, [score, size, label, showValue, animated]);

  return <svg ref={svgRef} width={size} height={size} />;
}

function getScoreColor(score: number): string {
  if (score >= 0.8) return '#ef4444';
  if (score >= 0.6) return '#f97316';
  if (score >= 0.4) return '#eab308';
  if (score >= 0.2) return '#22c55e';
  return '#06b6d4';
}
